import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OCR_STATUSES } from '@subs/shared';

import { bills, type BillFile } from '../api/client';
import { useToast } from './Toast';
import { Badge, Button, Drawer } from './ui';
import { formatBytes, formatDateTime } from '../lib/format';
import { hasExtractedFields, isConfirmed, resolveExtraction } from '../lib/bill-extraction';

const OCR_TONE: Record<string, 'ok' | 'warn' | 'bad' | 'neutral'> = {
  CONFIRMED: 'ok',
  EXTRACTED: 'warn',
  PENDING: 'neutral',
  PROCESSING: 'warn',
  FAILED: 'bad',
};

export function OcrBadge({ status, error }: { status: BillFile['ocrStatus']; error?: string | null }) {
  if (!status) return <span className="cell-muted">no OCR</span>;
  return (
    <span className="ocr-state">
      <Badge tone={OCR_TONE[status] ?? 'neutral'} title={error ?? undefined}>
        {status}
      </Badge>
    </span>
  );
}

/** Extraction fields a reviewer actually checks, in the order they check them. */
const EXTRACTED_FIELDS: { key: string; label: string; format?: (value: unknown) => string }[] = [
  { key: 'vendor', label: 'Vendor' },
  { key: 'date', label: 'Date' },
  { key: 'time', label: 'Time' },
  { key: 'amount', label: 'Amount' },
  { key: 'currency', label: 'Currency' },
  { key: 'fxRate', label: 'FX rate' },
  { key: 'commission', label: 'Commission' },
  { key: 'cardLast4', label: 'Card' , format: (value) => `····${String(value)}` },
  { key: 'txnNo', label: 'Receipt №' },
  { key: 'serviceHint', label: 'Service hint' },
];

function ExtractedFields({ json }: { json: Record<string, unknown> }) {
  const rows = EXTRACTED_FIELDS.map((field) => {
    const raw = json[field.key];
    if (raw === null || raw === undefined || raw === '') return null;
    return { label: field.label, value: field.format ? field.format(raw) : String(raw) };
  }).filter((row): row is { label: string; value: string } => row !== null);

  const extra = Object.keys(json).filter((key) => !EXTRACTED_FIELDS.some((field) => field.key === key));

  return (
    <>
      {rows.length > 0 ? (
        <dl className="dl extracted">
          {rows.map((row) => (
            <div className="extracted__row" key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        // A stored payload the reviewer recognises nothing in (the importer's
        // screenshot documents carry only `attachments`). The bill is NOT
        // "unextracted" — the raw JSON below is the payload.
        <p className="state__hint">
          This payload holds no recognised fields — see the raw JSON below.
        </p>
      )}
      {extra.length > 0 ? (
        <details style={{ marginTop: 'var(--space-2)' }}>
          <summary style={{ cursor: 'pointer', fontSize: 12, color: 'var(--text-secondary)' }}>
            {extra.length} more field{extra.length === 1 ? '' : 's'} and the raw JSON
          </summary>
          <pre className="bill-viewer__json" style={{ maxHeight: 220, marginTop: 'var(--space-2)' }}>
            {JSON.stringify(json, null, 2)}
          </pre>
        </details>
      ) : null}
    </>
  );
}

/**
 * The bill preview takes the whole drawer width, with the extracted JSON
 * stacked under it. The object is loaded through
 * the presigned redirect (`GET /bills/:id/content`), so the browser never sees
 * a bucket URL in the markup.
 *
 * `onBack` turns the drawer into a drill-in: the payment drawer renders the
 * bill INSTEAD of itself, so there is one overlay, one focus trap and one Esc
 * level — instead of a second overlay stacked on the first.
 */
export function BillViewer({
  billId,
  onClose,
  onBack,
  transactionOcrJson,
}: {
  billId: string | null;
  onClose: () => void;
  onBack?: () => void;
  /**
   * The linked transaction's `ocrJson`, when the caller already holds it —
   * the fallback payload for a file row with none (see `resolveExtraction`).
   */
  transactionOcrJson?: Record<string, unknown> | null;
}) {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const bill = useQuery({
    queryKey: ['bills', billId],
    queryFn: ({ signal }) => bills.get(billId ?? '', signal),
    enabled: Boolean(billId),
    // Poll while the pipeline is still moving.
    refetchInterval: (query) => {
      const status = query.state.data?.ocrStatus;
      return status === 'PENDING' || status === 'PROCESSING' ? 1_500 : false;
    },
  });

  const confirm = useMutation({
    mutationFn: () => bills.confirm(bill.data?.id ?? ''),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      queryClient.invalidateQueries({ queryKey: ['payments'] });
      toast(`Confirmed — ${updated.ocrStatus}`, 'success');
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const reprocess = useMutation({
    mutationFn: () => bills.reprocess(bill.data?.id ?? ''),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      toast('Queued for another OCR pass', 'success');
    },
    onError: (error: Error) => toast(error.message, 'error'),
  });

  const data = bill.data;
  // Fields come from BillFile.ocrJson (unwrapping the importer's `extraction`
  // wrapper), falling back to the linked transaction's mirror. EXTRACTED and
  // CONFIRMED both carry a payload — only EXTRACTED still needs the review
  // actions, so `canConfirm` stays EXTRACTED-only.
  const extracted = resolveExtraction(data?.ocrJson ?? null, transactionOcrJson);
  const showFields = hasExtractedFields(data?.ocrStatus, extracted);
  const confirmed = isConfirmed(data?.ocrStatus);
  const canConfirm = data?.ocrStatus === 'EXTRACTED';
  const inline = data ? ['image/png', 'image/jpeg', 'image/webp'].includes(data.mime) : false;

  return (
    <Drawer
      open={Boolean(billId)}
      onClose={onClose}
      title={data?.originalName ?? 'Bill'}
      description={data ? `${data.mime} · ${formatBytes(data.size)} · uploaded ${formatDateTime(data.createdAt)}` : undefined}
      footer={
        <>
          {onBack ? (
            <Button variant="secondary" size="sm" onClick={onBack}>
              Back to payment
            </Button>
          ) : null}
          <span style={{ flex: 1 }} />
          <Button variant="secondary" size="sm" loading={reprocess.isPending} onClick={() => reprocess.mutate()}>
            Reprocess
          </Button>
          <Button
            variant="primary"
            size="sm"
            loading={confirm.isPending}
            disabled={!canConfirm}
            onClick={() => confirm.mutate()}
          >
            Confirm extraction
          </Button>
        </>
      }
    >
      <div className="dl" style={{ marginBottom: 'var(--space-4)' }}>
        <dt>OCR status</dt>
        <dd>
          <OcrBadge status={data?.ocrStatus ?? null} error={data?.ocrError} />
        </dd>
        <dt>sha256</dt>
        <dd>
          <code>{data ? `${data.fileSha256.slice(0, 16)}…` : '—'}</code>
        </dd>
      </div>

      {data?.ocrError ? (
        <p className="state__hint" role="alert" style={{ color: 'var(--status-expired-fg)' }}>
          {data.ocrError}
        </p>
      ) : null}

      {data ? (
        <div className="bill-viewer">
          {/* The preview is the hero: it spans the whole drawer, and the
              reviewer reads the fields under it rather than beside it. */}
          <div className="bill-viewer__preview">
            {inline ? (
              <img
                className="bill-viewer__frame"
                alt={`Bill ${data.originalName ?? data.id}`}
                src={bills.contentUrl(data.id)}
              />
            ) : (
              <>
                {/* Chromium refuses `<object type="application/pdf">` but renders an
                    iframe with its native PDF viewer, so PDFs get a real preview. */}
                <iframe
                  className="bill-viewer__frame"
                  src={bills.contentUrl(data.id)}
                  title={`Bill ${data.originalName ?? data.id}`}
                />
                <div className="bill-viewer__empty" style={{ height: 'auto', marginTop: 'var(--space-2)' }}>
                  <span className="state__hint">
                    Blank above? Your browser blocked the inline PDF.
                  </span>
                  <a href={bills.contentUrl(data.id)} target="_blank" rel="noreferrer noopener">
                    Open the bill in a new tab
                  </a>
                </div>
              </>
            )}
          </div>
          <div className="bill-viewer__fields">
            <h3 className="section-title">
              {showFields ? 'Extracted fields' : 'Extraction'}
              {confirmed ? (
                <span style={{ marginLeft: 'var(--space-2)' }}>
                  <Badge tone="ok" title="This extraction was confirmed — the numbers are owned by a human.">
                    confirmed
                  </Badge>
                </span>
              ) : null}
            </h3>
            {showFields && extracted ? (
              <ExtractedFields json={extracted} />
            ) : extracted ? (
              // Payload present but the status does not show fields (e.g. a
              // FAILED row that kept its JSON). The error above explains why.
              <p className="state__hint">No fields are shown while the status is {data?.ocrStatus}.</p>
            ) : (
              <p className="state__hint">
                {data?.ocrStatus === 'FAILED'
                  ? 'The extraction failed — see the error above.'
                  : 'Nothing was extracted from this bill.'}
              </p>
            )}
          </div>
        </div>
      ) : null}
    </Drawer>
  );
}

export const OCR_STATUSES_ALL = OCR_STATUSES;
